import { type ReactNode } from 'react'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { ContentLayout, ContentMain } from '@/components/ContentLayout'
import styles from './BundleManagerPage.module.css'
import { BundleManagerHeader } from '@/features/admin/bundle/components/BundleManagerHeader'
import { type BundleArchivedFilterOptions } from '@/features/admin/bundle/BundleStore'
import { BundlesEmptyState } from '@/features/admin/bundle/components/BundlesEmptyState'
import { useElementSize } from '@/hooks/element-hooks'
import { useComposedRefs } from '@radix-ui/react-compose-refs'

export interface BundleManagerPageProps {
  /**
   * The workspace selector shown as the header's heading.
   */
  heading: ReactNode
  currentArchivedStatusTab: BundleArchivedFilterOptions
  onArchivedStatusTabChange: (tab: BundleArchivedFilterOptions) => void
  onAddBundle: () => void
  isDone: boolean
  hasBundles: boolean
  /**
   * `null` when all workspaces are shown.
   */
  currentWorkspaceName: string | null
  content: ReactNode
  /**
   * The infinite scroll loader, rendered until every bundle has been fetched.
   */
  loader: ReactNode
  /**
   * The bundle dialogs.
   */
  children?: ReactNode
}

export function BundleManagerPage({
  heading,
  currentArchivedStatusTab,
  onArchivedStatusTabChange,
  onAddBundle,
  isDone,
  hasBundles,
  currentWorkspaceName,
  content,
  loader,
  children,
}: BundleManagerPageProps) {
  const { connectRef: scrollRef, scrollYStart: scrollOffset } =
    useScrollOffsets()
  const canShowBundleManagerContent = isDone && hasBundles
  const canShowBundleEmptyState = isDone && !hasBundles
  const { size, connectRef } = useElementSize()
  const rootRef = useComposedRefs(scrollRef, connectRef)
  const isSmallScreen = size.width < 420

  return (
    <div className={styles.root} ref={rootRef}>
      <BundleManagerHeader
        scrollOffset={scrollOffset}
        heading={heading}
        currentArchivedStatusTab={currentArchivedStatusTab}
        onArchivedStatusTabChange={onArchivedStatusTabChange}
        onAddBundle={onAddBundle}
      />
      <ContentLayout
        accountForLabel
        accountForTabBar={!canShowBundleEmptyState}
        noPadding={canShowBundleEmptyState || isSmallScreen}
      >
        <ContentMain constrainWidth>
          <div className={styles.bundleViewer}>
            {canShowBundleManagerContent && content}
            {canShowBundleEmptyState && (
              <BundlesEmptyState
                currentWorkspaceName={currentWorkspaceName}
                currentArchivedStatusTab={currentArchivedStatusTab}
              />
            )}
            {!isDone && loader}
          </div>
        </ContentMain>
      </ContentLayout>
      {children}
    </div>
  )
}
